import React from 'react';
import { Link } from 'react-router-dom';

interface LinkifiedTextProps {
  text: string;
  className?: string;
}

// Stricter URL regex that only allows http/https protocols
const URL_REGEX = /\b(https?:\/\/[^\s<>"'{}|\\^`[\]]+)/gi;

// Mention regex - matches @username (alphanumeric, no spaces)
const MENTION_REGEX = /@([a-zA-Z0-9_]+)/g;

// Allowlist of safe URL patterns
const SAFE_URL_PATTERN = /^https?:\/\/[a-zA-Z0-9][-a-zA-Z0-9]*(\.[a-zA-Z0-9][-a-zA-Z0-9]*)+/;

function isValidAndSafeUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return false;
    }
    // Check against safe pattern
    return SAFE_URL_PATTERN.test(url);
  } catch {
    return false;
  }
}

interface TextPart {
  type: 'text' | 'url' | 'mention';
  content: string;
  username?: string;
}

function parseText(text: string): TextPart[] {
  const parts: TextPart[] = [];
  let lastIndex = 0;
  
  // Combined regex to find both URLs and mentions
  const combinedRegex = /(?:\b(https?:\/\/[^\s<>"'{}|\\^`[\]]+))|(?:@([a-zA-Z0-9_]+))/gi;
  
  let match;
  while ((match = combinedRegex.exec(text)) !== null) {
    // Add text before this match
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.slice(lastIndex, match.index)
      });
    }
    
    if (match[1]) {
      // URL match
      parts.push({
        type: 'url',
        content: match[1]
      });
    } else if (match[2]) {
      // Mention match
      parts.push({
        type: 'mention',
        content: match[0],
        username: match[2]
      });
    }
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.slice(lastIndex)
    });
  }
  
  return parts;
}

export function LinkifiedText({ text, className }: LinkifiedTextProps) {
  const parts = parseText(text);
  
  return (
    <span className={className}>
      {parts.map((part, index) => {
        if (part.type === 'url' && isValidAndSafeUrl(part.content)) {
          return (
            <a
              key={index}
              href={part.content}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline hover:text-primary/80 break-all"
              onClick={(e) => e.stopPropagation()}
            >
              {part.content}
            </a>
          );
        }
        
        if (part.type === 'mention' && part.username) {
          return (
            <span
              key={index}
              className="text-primary font-medium cursor-pointer hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                // Could navigate to user profile here if needed
              }}
            >
              {part.content}
            </span>
          );
        }
        
        return <React.Fragment key={index}>{part.content}</React.Fragment>;
      })}
    </span>
  );
}
